import {
  IFetchGraphqlUserCollection,
  ISingleWordpressCollection,
} from "resource-centre-theme/models";
import FileSaver from "file-saver";
import React from "react";
import { ApiClient } from "../api/ApiClient";

export const useDownloadCollection = () => {
  const [downloadInProgress, setDownloadInProgress] = React.useState(false);

  async function downloadCollection(collection: ISingleWordpressCollection) {
    if (downloadInProgress) return;

    setDownloadInProgress(true);

    const { default: JSZip } = await import("jszip");

    const zip = new JSZip();

    createCollectionFolder(zip, collection);

    zip
      .generateAsync({ type: "blob" })
      .then(blob => {
        FileSaver(blob, collection.slug);
        const client = new ApiClient(false);
        client.incrementDownloadCount(collection.databaseId);
      })
      .catch(err => new Error(err))
      .finally(() => {
        setDownloadInProgress(false);
      });
  }

  return { downloadCollection, downloadInProgress };
};

export const useDownloadLibrary = () => {
  const [downloadInProgress, setDownloadInProgress] = React.useState(false);

  async function downloadLibrary(
    library: IFetchGraphqlUserCollection,
    userCollections: IFetchGraphqlUserCollection[]
  ) {
    if (downloadInProgress) return;

    setDownloadInProgress(true);

    const { default: JSZip } = await import("jszip");

    const zip = new JSZip();

    createCollectionFolder(zip, library, "Posts");

    userCollections?.forEach(collection => {
      createCollectionFolder(
        zip,
        collection,
        `Collections/${collection.title}`
      );
    });

    zip
      .generateAsync({ type: "blob" })
      .then(blob => FileSaver(blob, "My library"))
      .catch(err => new Error(err))
      .finally(() => {
        setDownloadInProgress(false);
      });
  }

  return { downloadLibrary, downloadInProgress };
};

async function createCollectionFolder(
  zip: any,
  collection: ISingleWordpressCollection | IFetchGraphqlUserCollection,
  parentFolder?: string
) {
  const files = getCollectionFiles(collection);

  files.forEach(file => {
    const folder = zip.folder(
      !!parentFolder ? `${parentFolder}/${file.name}` : file.name
    );

    file.files.forEach(file => {
      const blobPromise = fetch(file.url).then(response => {
        if (response.status === 200 || response.status === 0) {
          return response.blob();
        } else {
          /**
           * TODO: HANDLE REJECTION
           */
          return Promise.reject(new Error(response.statusText));
        }
      });

      if (folder) {
        folder.file(`${file.name}.${extensions[file.mimeType]}`, blobPromise, {
          binary: true,
        });
      }
    });
  });

  return zip;
}

function getCollectionFiles(
  collection: ISingleWordpressCollection | IFetchGraphqlUserCollection
): {
  name: string;
  files: {
    name: string;
    mimeType: string;
    url: string;
  }[];
}[] {
  const documents =
    "userCollectionFields" in collection
      ? collection.userCollectionFields.collectionPosts
      : collection.collectionFields.documents;

  return documents
    ?.filter(post => post.nodeType == "Document")
    ?.map(post => ({
      name: post.title,
      files: post?.documentFields?.documents
        .filter(file => !!file.documentFile)
        .map(file => ({
          name: file?.documentFile?.title,
          mimeType: file?.documentFile?.mimeType,
          url: file?.documentFile?.mediaItemUrl,
        })),
    }));
}

const extensions: { [key: string]: string } = {
  "image/gif": "gif",
  "image/jpeg": "jpeg",
  "image/png": "png",
  "image/webp": "webp",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.oasis.opendocument.text": "odt",
  "application/pdf": "pdf",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/x-matroska": "mkv",
  "video/quicktime": "mov",
  "audio/mpeg": "mpeg",
};
